import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { SearchMovie, SearchTvShows } from "../constants/api";
import { Footer, Navbar } from "../components";
import AdsBannerTop from "../components/AdsBannerTop";
import SearchSkeleton from "../components/SearchSkeleton";
import { Star, Film, Tv, Search as SearchIcon, Play } from "lucide-react";

const Search = () => {
  const [searchDetails, setSearchDetails] = useState([]);
  const [searchTVDetails, setSearchTVDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("movies"); // 'movies' or 'tvshows'
  const { searchTerm } = useParams();

  useEffect(() => {
    const fetchSearchResults = async () => {
      setLoading(true);
      try {
        const [movieData, tvData] = await Promise.all([
          SearchMovie(`${searchTerm}`),
          SearchTvShows(`${searchTerm}`),
        ]);
        setSearchDetails(movieData?.results || []);
        setSearchTVDetails(tvData?.results || []);
      } catch (error) {
        console.error("Error fetching search results:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSearchResults();
  }, [searchTerm]);

  const decodedSearchTerm = decodeURIComponent(`${searchTerm}`);
  const totalResults = (searchDetails?.length || 0) + (searchTVDetails?.length || 0);
  const hasMovies = searchDetails?.length > 0;
  const hasTVShows = searchTVDetails?.length > 0;

  // Set default tab based on available results
  useEffect(() => {
    if (!loading) {
      if (activeTab === "movies" && !hasMovies && hasTVShows) {
        setActiveTab("tvshows");
      } else if (activeTab === "tvshows" && !hasTVShows && hasMovies) {
        setActiveTab("movies");
      }
    }
  }, [loading, hasMovies, hasTVShows, activeTab]);

  if (loading) {
    return (
      <>
        <Navbar />
        <AdsBannerTop />
        <SearchSkeleton />
        <Footer />
      </>
    );
  }

  if (totalResults === 0) {
    return (
      <div className="bg-black min-h-screen">
        <Navbar />
        <AdsBannerTop />
        <section className="padding pt-32 pb-16">
          <div className="max-w-2xl mx-auto text-center">
            <div className="mb-8">
              <SearchIcon className="text-white/20 mx-auto mb-4" size={80} />
            </div>
            <h1 className="text-white poppins text-3xl md:text-4xl font-bold mb-4">
              No Results Found
            </h1>
            <p className="text-white/60 mont text-lg mb-2">
              Oops! No results found for{" "}
              <span className="text-red font-semibold">"{decodedSearchTerm}"</span>
            </p>
            <p className="text-white/40 mont text-sm mt-4">
              Try searching with different keywords or check your spelling
            </p>
          </div>
        </section>
        <Footer />
      </div>
    );
  }
  return (
    <div className="bg-black min-h-screen">
      <Navbar />
      <AdsBannerTop />
      
      {/* Search Header */}
      <section className="padding pt-24 pb-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-1 w-12 bg-red"></div>
            <span className="text-red mont font-semibold text-sm uppercase tracking-wider">
              Search Results
            </span>
          </div>
          <h1 className="text-white poppins text-3xl md:text-4xl lg:text-5xl font-bold mb-2">
            Results for{" "}
            <span className="text-red">"{decodedSearchTerm}"</span>
          </h1>
          <p className="text-white/60 mont text-base md:text-lg mb-6">
            Found {totalResults} result{totalResults !== 1 ? 's' : ''}
            {hasMovies && hasTVShows && (
              <span> • {searchDetails.length} Movies • {searchTVDetails.length} TV Shows</span>
            )}
          </p>

          {/* Tabs */}
          {hasMovies && hasTVShows && (
            <div className="inline-flex bg-gray/20 backdrop-blur-sm border border-white/10 rounded-lg p-1">
              <button
                onClick={() => setActiveTab("movies")}
                className={`px-6 py-2.5 rounded-md mont font-semibold text-sm transition-all duration-300 flex items-center gap-2 ${
                  activeTab === "movies"
                    ? "bg-red text-white shadow-lg shadow-red/30"
                    : "text-white/60 hover:text-white"
                }`}
              >
                <Film size={18} />
                Movies ({searchDetails.length})
              </button>
              <button
                onClick={() => setActiveTab("tvshows")}
                className={`px-6 py-2.5 rounded-md mont font-semibold text-sm transition-all duration-300 flex items-center gap-2 ${
                  activeTab === "tvshows"
                    ? "bg-red text-white shadow-lg shadow-red/30"
                    : "text-white/60 hover:text-white"
                }`}
              >
                <Tv size={18} />
                TV Shows ({searchTVDetails.length})
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Movies Results */}
      {(activeTab === "movies" || !hasTVShows) && hasMovies && (
        <section className="padding pb-16">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-6 md:grid-cols-4 sm:grid-cols-3 grid-cols-2 gap-4 md:gap-6">
              {searchDetails?.filter(movie => movie.poster_path).map((movie) => (
                <Link
                  key={movie?.id}
                  to={`/movies/${movie?.id}`}
                  className="group modern-movie-card"
                >
                  <div className="relative overflow-hidden rounded-lg bg-gray/30 movie-card-wrapper">
                    {/* Movie Poster */}
                    <div className="relative aspect-[2/3] overflow-hidden bg-gradient-to-br from-gray/40 to-gray/20">
                      <img
                        src={`https://image.tmdb.org/t/p/w500${movie?.poster_path}`}
                        alt={movie?.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                      {/* Placeholder when image fails */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray/40 to-gray/20 p-4" style={{ display: 'none' }}>
                        <Film className="text-white/40 mb-2" size={40} />
                        <p className="text-white/60 mont text-xs text-center line-clamp-2">{movie?.title}</p>
                      </div>
                      
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      
                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                          <Play className="text-white" size={28} fill="white" />
                        </div>
                      </div>

                      {/* Rating Badge */}
                      <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
                        <Star className="text-yellow-400" size={12} fill="#FBBF24" />
                        <span className="text-white mont text-xs font-semibold">
                          {movie?.vote_average?.toFixed(1)}
                        </span>
                      </div>
                    </div>

                    {/* Movie Info */}
                    <div className="p-3">
                      <h3 className="text-white mont font-semibold text-sm leading-tight line-clamp-2 group-hover:text-red transition-colors duration-300">
                        {movie?.title}
                      </h3>
                      {movie?.release_date && (
                        <p className="text-white/50 mont text-xs mt-1">
                          {new Date(movie.release_date).getFullYear()}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TV Shows Results */}
      {(activeTab === "tvshows" || !hasMovies) && hasTVShows && (
        <section className="padding pb-16">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-6 md:grid-cols-4 sm:grid-cols-3 grid-cols-2 gap-4 md:gap-6">
              {searchTVDetails?.filter(show => show.poster_path).map((tvShow) => (
                <Link
                  key={tvShow?.id}
                  to={`/tvshows/${tvShow?.id}`}
                  className="group modern-movie-card"
                >
                  <div className="relative overflow-hidden rounded-lg bg-gray/30 movie-card-wrapper">
                    {/* TV Show Poster */}
                    <div className="relative aspect-[2/3] overflow-hidden bg-gradient-to-br from-gray/40 to-gray/20">
                      <img
                        src={`https://image.tmdb.org/t/p/w500${tvShow?.poster_path}`}
                        alt={tvShow?.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                      {/* Placeholder when image fails */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray/40 to-gray/20 p-4" style={{ display: 'none' }}>
                        <Tv className="text-white/40 mb-2" size={40} />
                        <p className="text-white/60 mont text-xs text-center line-clamp-2">{tvShow?.name}</p>
                      </div>
                      
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      
                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                          <Play className="text-white" size={28} fill="white" />
                        </div>
                      </div>

                      {/* Rating Badge */}
                      <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
                        <Star className="text-yellow-400" size={12} fill="#FBBF24" />
                        <span className="text-white mont text-xs font-semibold">
                          {tvShow?.vote_average?.toFixed(1)}
                        </span>
                      </div>
                    </div>

                    {/* TV Show Info */}
                    <div className="p-3">
                      <h3 className="text-white mont font-semibold text-sm leading-tight line-clamp-2 group-hover:text-red transition-colors duration-300">
                        {tvShow?.name}
                      </h3>
                      {tvShow?.first_air_date && (
                        <p className="text-white/50 mont text-xs mt-1">
                          {new Date(tvShow.first_air_date).getFullYear()}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default Search;
